import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CalendarView } from "../../features/calendar/models/CalendarView";
import { Instructor } from "../../features/instructors/models/Instructor";

interface CalendarToolbarProps {
  rangeLabel: string;
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  onPrevious: () => void;
  onToday: () => void;
  onNext: () => void;
  /** Active colleagues; the instructor filter renders only when there is more than one. */
  instructors?: Instructor[];
  instructorFilter?: string | null;
  onInstructorFilterChange?: (instructorId: string | null) => void;
}

/**
 * Range navigation (previous / today / next), the week ↔ month toggle and —
 * in a multi-instructor organization — the instructor filter.
 */
const CalendarToolbar: React.FC<CalendarToolbarProps> = ({
  rangeLabel,
  view,
  onViewChange,
  onPrevious,
  onToday,
  onNext,
  instructors = [],
  instructorFilter = null,
  onInstructorFilterChange,
}) => {
  const { t } = useTranslation();

  const navClass =
    "flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-grey-30 transition-colors hover:bg-grey-95 dark:text-grey-80 dark:hover:bg-primary-20";

  const toggleClass = (active: boolean) =>
    `cursor-pointer rounded-lg px-3 py-1.5 text-sm transition-colors ${
      active
        ? "bg-primary-40 text-white"
        : "text-grey-30 hover:bg-grey-95 dark:text-grey-80 dark:hover:bg-primary-20"
    }`;

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onPrevious}
          aria-label={t("calendar.previous")}
          className={navClass}
        >
          <ChevronLeft size={18} />
        </button>
        <button
          type="button"
          onClick={onToday}
          className="cursor-pointer rounded-lg px-3 py-1.5 text-sm text-grey-30 transition-colors hover:bg-grey-95 dark:text-grey-80 dark:hover:bg-primary-20"
        >
          {t("calendar.today")}
        </button>
        <button
          type="button"
          onClick={onNext}
          aria-label={t("calendar.next")}
          className={navClass}
        >
          <ChevronRight size={18} />
        </button>
        <span className="ms-2 text-lg font-semibold text-grey-20 dark:text-white">{rangeLabel}</span>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {instructors.length > 1 && (
          <select
            aria-label={t("instructors.filterAria")}
            className="input py-1.5 text-sm"
            value={instructorFilter ?? ""}
            onChange={(e) =>
              onInstructorFilterChange?.(e.target.value === "" ? null : e.target.value)
            }
          >
            <option value="">{t("instructors.filterAll")}</option>
            {instructors.map((instructor) => (
              <option key={instructor.id} value={instructor.id}>
                {instructor.fullName}
              </option>
            ))}
          </select>
        )}
        <div className="with-border flex items-center gap-1 rounded-xl p-1">
          <button type="button" onClick={() => onViewChange("week")} className={toggleClass(view === "week")}>
            {t("calendar.week")}
          </button>
          <button type="button" onClick={() => onViewChange("month")} className={toggleClass(view === "month")}>
            {t("calendar.month")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarToolbar;
